import {
  cbaIntegrationAPIRoot,
  cutglueAPIRoot,
  fsioAPIRoot,
  mediaStoreAPIRoot,
  steeringAPIRoot,
} from '@/configuration'
import type { AnyRelations } from '@/types'
import { areSetsEqual } from '@/util'
import type { APIObject } from '@rokoli/bnb'
import { createURLBuilder } from '@rokoli/bnb/common'
import type { PartialUpdateOperation } from '@rokoli/bnb/drf'
import { MaybeRefOrGetter, toValue } from 'vue'

export const createCutGlueURL = createURLBuilder(cutglueAPIRoot, false)
export const createCBAIntegrationURL = createURLBuilder(cbaIntegrationAPIRoot, false)
export const createFSIOURL = createURLBuilder(fsioAPIRoot, false)
export const createMediaStoreURL = createURLBuilder(mediaStoreAPIRoot, false)
export const createSteeringURL = createURLBuilder(steeringAPIRoot)

export function createRelationManager<T extends APIObject, TData extends Partial<AnyRelations<T>>>(
  partialUpdate: PartialUpdateOperation<T, TData>,
  getObject?: MaybeRefOrGetter<T>,
) {
  return function (data: Partial<AnyRelations<T>>, object?: T): Promise<T> {
    const obj = typeof object !== 'undefined' ? object : toValue(getObject)
    const updateData: Partial<AnyRelations<T>> = {}

    if (typeof obj === 'undefined') {
      throw new TypeError('You must either pass an object getter or pass the object on update.')
    }

    for (const _key of Object.keys(data)) {
      const key = _key as keyof typeof updateData
      const currentValue = obj[key]
      const newValue = data[key]

      if (!Array.isArray(currentValue) || !Array.isArray(newValue)) {
        const message = `${_key} is not a relation on object`
        console.error(message, obj)
        throw new TypeError(message)
      }

      if (!areSetsEqual(currentValue, newValue)) {
        // @ts-expect-error TODO: I would like to know how to fix this.
        updateData[key] = newValue
      }
    }

    if (Object.keys(updateData).length > 0) {
      return partialUpdate(obj.id, updateData as TData)
    } else {
      return Promise.resolve(obj as T)
    }
  }
}
